import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  PaywallContext,
  type PaywallContextValue,
  type PaywallRequest,
} from "./paywall-context";
import PaywallModal from "../components/billing/PaywallModal";
import type { PaywallError, PlanTier } from "../services/billingApi";
import { useEntitlements } from "../hooks/useEntitlements";
import {
  FEATURE_BLURB,
  FEATURE_TITLE,
  suggestedTiersForFeature,
  type GatedFeature,
} from "../data/planRules";

const ALL_PAID_TIERS: PlanTier[] = [
  "BASIC",
  "PREMIUM",
  "PLATINUM",
  "ULTRA_PRO",
];

/** Copy for the two download-specific refusals the server can return. */
const DOWNLOAD_COPY: Record<string, { title: string; description: string }> = {
  TEMPLATE_TIER_TOO_LOW: {
    title: "Unlock this design",
    description:
      "Pick a plan that includes this template to export your high-resolution PDF.",
  },
  NO_DOWNLOAD_CREDITS: {
    title: "You're out of download credits",
    description:
      "Top up to keep exporting. Credits never expire and stack with what you already own.",
  },
  AI_AGENT_QUOTA_EXCEEDED: {
    title: "Monthly AI Career Assistant quota reached",
    description:
      "Upgrade your plan for a larger allowance, or wait for your quota to reset.",
  },
};

/**
 * Single upgrade modal for the whole app.
 *
 * Any component can raise it — from a server 403, from a locked feature, or
 * with custom copy — so paywall behaviour stays consistent wherever the user
 * hits a limit.
 */
export function PaywallProvider({ children }: { children: ReactNode }) {
  const { entitlements } = useEntitlements();
  const [request, setRequest] = useState<PaywallRequest | null>(null);

  const openPaywall = useCallback((next: PaywallRequest) => {
    setRequest(next);
  }, []);

  const closePaywall = useCallback(() => setRequest(null), []);

  const openFeaturePaywall = useCallback(
    (feature: GatedFeature, returnPath?: string) => {
      setRequest({
        title: FEATURE_TITLE[feature],
        description: FEATURE_BLURB[feature],
        suggestedTiers: suggestedTiersForFeature(feature),
        feature,
        returnPath,
      });
    },
    [],
  );

  const openPaywallFromError = useCallback(
    (
      error: PaywallError,
      returnPath?: string,
      onUseLegacy?: () => void,
      templateKey?: string,
    ) => {
      // A locked feature has richer standing copy than the terse server string.
      if (error.code === "FEATURE_LOCKED" && error.feature) {
        openFeaturePaywall(error.feature, returnPath);
        return;
      }

      const copy = DOWNLOAD_COPY[error.code];
      const isBasicDesignBlock =
        error.code === "TEMPLATE_TIER_TOO_LOW" &&
        (error.currentTier ?? entitlements?.planTier) === "BASIC";
      setRequest({
        title: isBasicDesignBlock
          ? "Basic Pass covers the Legacy design only"
          : (copy?.title ?? "Upgrade required"),
        notice: isBasicDesignBlock ? "basic-design" : undefined,
        description: isBasicDesignBlock
          ? `Your one-time Basic Pass payment covers PDF downloads using Classic Legacy. The selected paid design is not included. No download credit was used for this attempt. ${
              (error.remainingCredits ?? entitlements?.downloadCredits ?? 0) > 0
                ? "Switch to Classic Legacy to use your remaining download credit, or choose Premium or above for this design."
                : "You also have no download credits remaining. Choose a plan that includes the design you want."
            }`
          : error.code === "TEMPLATE_TIER_TOO_LOW" ||
              error.code === "NO_DOWNLOAD_CREDITS"
            ? error.message
            : (copy?.description ?? error.message),
        suggestedTiers: error.suggestedTiers ?? ALL_PAID_TIERS,
        feature: error.feature,
        returnPath,
        templateKey,
        onUseLegacy: onUseLegacy,
      });
    },
    [openFeaturePaywall, entitlements],
  );

  const value = useMemo<PaywallContextValue>(
    () => ({
      openPaywall,
      openPaywallFromError,
      openFeaturePaywall,
      closePaywall,
      isOpen: request !== null,
    }),
    [
      openPaywall,
      openPaywallFromError,
      openFeaturePaywall,
      closePaywall,
      request,
    ],
  );

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PaywallModal request={request} onClose={closePaywall} />
    </PaywallContext.Provider>
  );
}
